// Cookie consent — banner UI + preference page.
//
// One decision per visitor (single domain-wide cookie), shared across all markets/languages.
// Activation of the gated scripts is owned by the BLOCKING inline script in <head> (see
// app/parts/head.php), which injects granted scripts into <head> as early as possible and
// exposes window.__webteroConsentActivate. This module only handles the banner UI, writing
// the consent cookie, and the settings page.
//
// Expiry: a "positive" decision (at least one non-necessary category granted) is remembered
// for a year; a full rejection (nothing granted) is remembered for only a day, so the banner
// asks again the next day instead of staying silent for a year.

const COOKIE = 'webtero_cookie_consent';
const VERSION = 1;
const YEAR_DAYS = 365;
const REJECT_DAYS = 1;
const CATEGORIES = ['functional', 'analytics', 'marketing']; // `necessary` is implicit/always-on

function readConsent() {
  const match = document.cookie.match(/(?:^|;\s*)webtero_cookie_consent=([^;]*)/);
  if (!match) return null;
  try {
    const obj = JSON.parse(decodeURIComponent(match[1]));
    if (!obj || obj.v !== VERSION) return null;
    return obj;
  } catch (e) {
    return null;
  }
}

function writeConsent(state) {
  const obj = { v: VERSION, ts: Math.floor(Date.now() / 1000) };
  CATEGORIES.forEach((cat) => {
    obj[cat] = state[cat] ? 1 : 0;
  });
  // Positive decision → remember a year; rejection (nothing granted) → re-ask tomorrow.
  const anyGranted = CATEGORIES.some((cat) => obj[cat]);
  const maxAge = (anyGranted ? YEAR_DAYS : REJECT_DAYS) * 86400;
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  // Scope the cookie to the main registrable domain (e.g. .webtero.com) when set by PHP, so
  // one decision is shared across all webtero.* subdomains (main site ↔ microsite funnels).
  // Empty on separate-domain microsites → cookie stays host-scoped (browser isolation).
  const domain = window.__webteroCookieDomain ? `; Domain=${window.__webteroCookieDomain}` : '';
  document.cookie =
    `${COOKIE}=${encodeURIComponent(JSON.stringify(obj))}` +
    `; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}${domain}`;
  // Ungated third-party snippets in custom_html_head (Sklik retargeting/conversion, …) read
  // window.webteroConsent at load time, when the visitor usually hasn't decided yet and the
  // hit goes out with consent 0. This lets them re-send with 1 the moment consent arrives.
  // Gated scripts don't need it — they aren't injected until their category is granted.
  document.dispatchEvent(new CustomEvent('webtero:consent', { detail: obj }));
  return obj;
}

export function cookieConsent() {
  // Banner — shown only when the visitor hasn't decided yet.
  const banner = document.querySelector('[data-cookie-banner]');
  if (banner) {
    if (!readConsent()) {
      // Defer one frame so the slide-up transition plays.
      requestAnimationFrame(() => banner.classList.add('is-visible'));
    }

    banner.querySelector('[data-cc-accept]')?.addEventListener('click', () => {
      writeConsent({ functional: 1, analytics: 1, marketing: 1 });
      window.__webteroConsentActivate?.(); // inject now-granted scripts into <head>
      banner.classList.remove('is-visible');
    });

    banner.querySelector('[data-cc-reject]')?.addEventListener('click', () => {
      writeConsent({ functional: 0, analytics: 0, marketing: 0 }); // re-ask tomorrow
      banner.classList.remove('is-visible');
    });
    // [data-cc-settings] is a plain link to the cookie page — no JS needed.
  }

  // Cookie settings page.
  //  - Turning a category ON saves immediately (and injects its script live).
  //  - Turning a category OFF is deferred: the Save button lights up and applies the
  //    revocation on click (with a reload, since already-run scripts can't be unloaded).
  const panel = document.querySelector('[data-cookie-settings]');
  if (panel) {
    const saveBtn = panel.querySelector('[data-cc-save]');
    const toggles = {};
    CATEGORIES.forEach((cat) => {
      toggles[cat] = panel.querySelector(`[data-cc-toggle="${cat}"]`);
    });

    // `saved` mirrors the persisted decision; toggles start from it (default all off).
    const consent = readConsent();
    const saved = {};
    CATEGORIES.forEach((cat) => {
      saved[cat] = !!(consent && consent[cat]);
      if (toggles[cat]) toggles[cat].checked = saved[cat];
    });

    const isDirty = () =>
      CATEGORIES.some((cat) => !!(toggles[cat] && toggles[cat].checked) !== saved[cat]);

    const refreshButton = () => {
      if (saveBtn) saveBtn.disabled = !isDirty();
    };

    refreshButton(); // disabled by default — only lights up on a pending change (a disable)

    CATEGORIES.forEach((cat) => {
      const toggle = toggles[cat];
      if (!toggle) return;
      toggle.addEventListener('change', () => {
        if (toggle.checked && !saved[cat]) {
          // Enabling → auto-save now + inject the newly granted script live.
          saved[cat] = true;
          writeConsent(saved);
          window.__webteroConsentActivate?.();
          banner?.classList.remove('is-visible');
        }
        // Disabling (or reverting) is applied via the Save button.
        refreshButton();
      });
    });

    saveBtn?.addEventListener('click', () => {
      if (saveBtn.disabled) return;
      const state = {};
      CATEGORIES.forEach((cat) => {
        state[cat] = !!(toggles[cat] && toggles[cat].checked);
      });
      writeConsent(state);
      location.reload(); // reload re-runs the head activator without the revoked scripts
    });
  }
}
